import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Paper,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ApiService from "../../../services/categoryManagementApi";

const { reportsAndAnalyticsApi } = ApiService;

// UTC helpers
function toUTCStart(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}
function toUTCEnd(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}
// Period
const getStartEndDates = (type, month, year) => {
  if (type === "yearly") {
    const start = new Date(Date.UTC(year, 0, 1));
    const end = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
    return [start, end];
  }
  // monthly
  const start = new Date(Date.UTC(year, month, 1));
  const end = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));
  return [start, end];
};

const RevenueReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodType, setPeriodType] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i,
    label: new Date(0, i).toLocaleString("default", { month: "long" }),
  }));

  const periodDisabled = Boolean(fromDate && toDate);
  const dateDisabled = Boolean(periodType);

  // --- OnChange handlers
  const onChangeFromDate = (newDate) => {
    setFromDate(newDate);
    // Auto clear period if using dates
    if (newDate) {
      setPeriodType("");
      setSelectedYear("");
      setSelectedMonth("");
      // Reset end date if now before start
      if (toDate && newDate && toDate < newDate) setToDate(null);
    }
  };

  const onChangeToDate = (newDate) => {
    setToDate(newDate);
    if (newDate) {
      setPeriodType("");
      setSelectedYear("");
      setSelectedMonth("");
    }
  };

  const onChangePeriodType = (e) => {
    setPeriodType(e.target.value);
    setSelectedMonth("");
    setSelectedYear("");
    setFromDate(null);
    setToDate(null);
  };

  const onChangeMonth = (e) => setSelectedMonth(e.target.value);
  const onChangeYear = (e) => setSelectedYear(e.target.value);

  // --- Validation logic
  const isDateRangeValid =
    fromDate && toDate && !isNaN(new Date(fromDate)) && !isNaN(new Date(toDate)) && toDate >= fromDate;

  const isPeriodValid =
    periodType === "yearly"
      ? selectedYear
      : periodType === "monthly"
        ? selectedYear && selectedMonth !== ""
        : false;

  // --- Submit logic
 const roundNumbers = (dataArray) => {
  return dataArray.map(item => {
    const formattedItem = {};
    for (const key in item) {
      if (typeof item[key] === "number") {
        formattedItem[key] = parseFloat(item[key].toFixed(2));
      } else {
        formattedItem[key] = item[key];
      }
    }
    return formattedItem;
  });
};

const fetchReport = async () => {
  let startDate, endDate;

  if (fromDate && toDate) {
    if (!isDateRangeValid) {
      toast.error("End date cannot be before start date.");
      return;
    }
    startDate = toUTCStart(new Date(fromDate));
    endDate = toUTCEnd(new Date(toDate));
  } else if (periodType && isPeriodValid) {
    [startDate, endDate] = getStartEndDates(
      periodType,
      +selectedMonth,
      +selectedYear
    );
  } else {
    toast.error("Please select a valid date range or a period.");
    return;
  }

  const payload = {
    type: "revenue",
    period: periodType,
    startDate: startDate ? startDate.toISOString() : undefined,
    endDate: endDate ? endDate.toISOString() : undefined,
  };

  setLoading(true);
  try {
    const response = await reportsAndAnalyticsApi.getUserReport(payload);
    const result = response?.data?.data?.result || {};

    const formattedResult = {
      dailyRevenue: roundNumbers(result.dailyRevenue || []),
      monthlyRevenue: roundNumbers(result.monthlyRevenue || [])
    };

    setReportData(formattedResult);
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to fetch revenue report"
    );
    console.error("Error fetching revenue report:", error);
  } finally {
    setLoading(false);
  }
};


 const exportToExcel = (data, label = "Revenue") => {
  const formattedData = (data || []).map(item => ({
    ...item,
    totalSales: item.totalSales ? parseFloat(item.totalSales).toFixed(2) : "",
    avgOrderValue: item.avgOrderValue ? parseFloat(item.avgOrderValue).toFixed(2) : ""
  }));

  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `${label} Report`);

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  const fileName = `${label}_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;

  saveAs(blob, fileName);
};


  const formatMonthlyRevenueData = (data = []) =>
    data.map((item) => {
      let label = "";
      if (item.year && item.month) {
        const date = new Date(item.year, item.month - 1);
        label = `${date.toLocaleString("default", { month: "short" })} ${item.year}`;
      }
      return {
        name: label,
        totalSales: item.totalSales.toFixed(2) ?? 0,
        totalOrders: item.totalOrders ?? 0,
        avgOrderValue: item.avgOrderValue.toFixed(2) ?? 0,
      };
    });

  const formatDailyRevenueData = (data = []) =>
    data.map((item) => {
      const dateObj = new Date(item.date);
      const day = dateObj.getDate();
      const monthShort = dateObj.toLocaleString("default", { month: "short" });
      return {
        ...item,
        name: `${day} ${monthShort}`,
      };
    });

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setPeriodType("");
    setSelectedYear("");
    setSelectedMonth("");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={{ xs: 2, sm: 3 }}>
        <Typography variant="h5" gutterBottom>
          Revenue Report
        </Typography>

        <Grid container spacing={2} mb={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={onChangeFromDate}
              disabled={dateDisabled}
              maxDate={toDate || undefined}
              slotProps={{
                textField: { fullWidth: true, variant: "outlined" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={onChangeToDate}
              disabled={dateDisabled}
              minDate={fromDate || undefined}
              maxDate={new Date()}
              slotProps={{
                textField: { fullWidth: true, variant: "outlined" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              select
              label="Period Type"
              value={periodType}
              onChange={onChangePeriodType}
              disabled={periodDisabled}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
          </Grid>
          {periodType && !periodDisabled && (
            <>
              {periodType === "monthly" && (
                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Month"
                    value={selectedMonth}
                    onChange={onChangeMonth}
                  >
                    {monthOptions.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={4} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Year"
                  value={selectedYear}
                  onChange={onChangeYear}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchReport}
              disabled={loading || (!isDateRangeValid && !isPeriodValid)}
              sx={{ height: 48 }}
            >
              {loading ? "Loading..." : "Generate Report"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              sx={{ height: 48 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {reportData === null ? (
          <Paper
            elevation={2}
            sx={{
              mt: 4,
              width: "100%",
              minHeight: "35vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fafbfc",
              boxSizing: "border-box",
              p: { xs: 2, sm: 4 },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Select a date range or a period, then click <strong>Generate Report</strong> to view the charts.
            </Typography>

          </Paper>
        ) : (
          <Box mt={4}>
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Grid item>
                <Typography variant="h6">Revenue Report</Typography>
              </Grid>
              <Grid item>
                <Grid container spacing={1}>
                  {reportData?.dailyRevenue?.length > 0 && (
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          exportToExcel(reportData.dailyRevenue, "DailyRevenue")
                        }
                      >
                        Export Daily
                      </Button>
                    </Grid>
                  )}
                  {reportData?.monthlyRevenue?.length > 0 && (
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          exportToExcel(
                            reportData.monthlyRevenue,
                            "MonthlyRevenue"
                          )
                        }
                      >
                        Export Monthly
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {/* Charts */}
            {reportData?.dailyRevenue?.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Daily Revenue Summary
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={formatDailyRevenueData(reportData.dailyRevenue)}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="totalSales"
                      stroke="#1976d2"
                      name="Total Sales"
                    />
                    <Line
                      type="monotone"
                      dataKey="totalOrders"
                      stroke="#82ca9d"
                      name="Total Orders"
                    />
                    <Line
                      type="monotone"
                      dataKey="avgOrderValue"
                      stroke="#8884d8"
                      name="Avg Order Value"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
            {reportData?.monthlyRevenue?.length > 0 && (
              <>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ mt: 4 }}
                >
                  Monthly Revenue
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={formatMonthlyRevenueData(reportData.monthlyRevenue)}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="totalSales"
                      stroke="#1976d2"
                      name="Total Sales"
                    />
                    <Line
                      type="monotone"
                      dataKey="totalOrders"
                      stroke="#82ca9d"
                      name="Total Orders"
                    />
                    <Line
                      type="monotone"
                      dataKey="avgOrderValue"
                      stroke="#8884d8"
                      name="Avg Order Value"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Box>
        )}

      </Box>
    </LocalizationProvider>
  );
};

export default RevenueReport;
